import { useQuery } from '@tanstack/react-query';
import { dashboardService } from '../services/dashboard-service';
import {
  Building2,
  Users,
  MapPin,
  Package,
  Calendar,
  TrendingUp,
  Activity,
} from 'lucide-react';

export function Dashboard() {
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardService.getStats(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-ink-muted">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-brand-warm/20 border border-brand-warm/40 text-brand-warm px-4 py-3 rounded">
        Error loading dashboard: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const entityCards = [
    {
      title: 'Businesses',
      icon: Building2,
      data: stats.businesses,
      color: 'blue',
    },
    {
      title: 'Trainers',
      icon: Users,
      data: stats.trainers,
      color: 'green',
    },
    {
      title: 'Facilities',
      icon: MapPin,
      data: stats.facilities,
      color: 'purple',
    },
    {
      title: 'Products',
      icon: Package,
      data: stats.products,
      color: 'orange',
    },
    {
      title: 'Trainings',
      icon: Calendar,
      data: stats.trainings,
      color: 'pink',
    },
  ];

  const colorClasses: Record<string, string> = {
    blue: 'bg-brand-primary/20 text-brand-primary-light',
    green: 'bg-brand-confirm/20 text-brand-confirm',
    purple: 'bg-brand-primary-light/20 text-brand-primary-light',
    orange: 'bg-brand-accent/20 text-brand-accent',
    pink: 'bg-brand-warm/20 text-brand-warm',
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="font-display text-2xl font-semibold tracking-tight text-ink-primary">Platform Overview</h2>
        <p className="text-ink-secondary mt-1">Real-time statistics and insights</p>
      </div>

      {/* Entity Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {entityCards.map((card) => (
          <div key={card.title} className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-ink-secondary">{card.title}</h3>
              <div className={`p-2 rounded-lg ${colorClasses[card.color]}`}>
                <card.icon size={20} />
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold text-ink-primary">{card.data.total}</p>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-brand-confirm">
                  {card.data.active} active
                </span>
                <span className="text-ink-muted">
                  {card.data.disabled} disabled
                </span>
              </div>
              <div className="text-xs text-ink-muted pt-2 border-t border-white/10">
                +{card.data.newThisMonth} this month
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Revenue & Platform Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Card */}
        <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-brand-confirm/20 text-brand-confirm">
              <TrendingUp size={24} />
            </div>
            <h3 className="font-display text-lg font-semibold tracking-tight text-ink-primary">Revenue</h3>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-ink-secondary mb-1">Total Revenue</p>
              <p className="text-2xl font-bold text-ink-primary">
                PLN {stats.revenue.totalRevenue.toLocaleString('pl-PL', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
              <div>
                <p className="text-xs text-ink-secondary mb-1">This Month</p>
                <p className="text-lg font-semibold text-ink-primary">
                  PLN {stats.revenue.thisMonth.toLocaleString('pl-PL', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-xs text-ink-secondary mb-1">Total Payments</p>
                <p className="text-lg font-semibold text-ink-primary">
                  {stats.revenue.totalPayments}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Platform Stats Card */}
        <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-brand-primary/20 text-brand-primary-light">
              <Activity size={24} />
            </div>
            <h3 className="font-display text-lg font-semibold tracking-tight text-ink-primary">Platform Activity</h3>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-ink-secondary mb-1">Total Users</p>
              <p className="text-2xl font-bold text-ink-primary">
                {stats.platform.totalUsers.toLocaleString()}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
              <div>
                <p className="text-xs text-ink-secondary mb-1">Active This Month</p>
                <p className="text-lg font-semibold text-ink-primary">
                  {stats.platform.activeUsersThisMonth}
                </p>
              </div>
              <div>
                <p className="text-xs text-ink-secondary mb-1">Reservations</p>
                <p className="text-lg font-semibold text-ink-primary">
                  {stats.platform.totalReservations}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      {stats.recentActivity && stats.recentActivity.length > 0 && (
        <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm">
          <div className="px-6 py-4 border-b border-white/10">
            <h3 className="font-display text-lg font-semibold tracking-tight text-ink-primary">Recent Admin Activity</h3>
          </div>
          <div className="divide-y divide-white/10">
            {stats.recentActivity.map((activity) => (
              <div key={activity.id} className="px-6 py-4 hover:bg-white/5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-ink-primary">{activity.action}</p>
                    <p className="text-sm text-ink-secondary mt-1">
                      {activity.entityType} • {activity.adminUserEmail}
                    </p>
                    {activity.reason && (
                      <p className="text-xs text-ink-muted mt-1">Reason: {activity.reason}</p>
                    )}
                  </div>
                  <div className="text-xs text-ink-muted whitespace-nowrap ml-4">
                    {new Date(activity.timestamp).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
